import { useMemo } from 'react';
import { NormalizedTask } from '../../domain/normalize';
import { computeKpis, bucketize, sumBy, countBy, mean } from '../../domain/metrics';
import { UserDoc } from '../../domain/types';
import { fmtDia } from '../../domain/util';
import { ESTADO_ENTREGA_LABELS, ESTADO_ENTREGA_COLORS, esEntregado } from '../../domain/config';
import { Card, Kpi, BarCard, BoardIntro } from './shared';

const EDGES = [1, 3, 7, 14, 30, Infinity];
const LABELS = ['≤1d', '2-3d', '4-7d', '1-2sem', '2-4sem', '>1mes'];

export default function BoardTiempos({ tasks }: { tasks: NormalizedTask[]; users: UserDoc[] }) {
  const k = useMemo(() => computeKpis(tasks), [tasks]);
  const activos = useMemo(() => tasks.filter((t) => !t.excluido), [tasks]);

  const demoraHist = useMemo(
    () => bucketize(activos.map((t) => t.dias_demora).filter((d): d is number => d != null && d >= 0), EDGES, LABELS),
    [activos]
  );
  const antelHist = useMemo(
    () => bucketize(activos.map((t) => t.dias_antelacion).filter((d): d is number => d != null).map((d) => Math.max(0, d)), EDGES, LABELS),
    [activos]
  );

  // Demora promedio por espacio y por área (solo entregadas con demora válida)
  const entregadas = activos.filter((t) => esEntregado(t.estado) && t.dias_demora != null && t.dias_demora >= 0);
  const demoraPorEspacio = useMemo(() => {
    const grupos = new Map<string, number[]>();
    entregadas.forEach((t) => { const a = grupos.get(t.solicitante) || []; a.push(t.dias_demora!); grupos.set(t.solicitante, a); });
    return Array.from(grupos, ([name, arr]) => ({ name, value: mean(arr) || 0 })).sort((a, b) => b.value - a.value);
  }, [entregadas]);
  const demoraPorArea = useMemo(() => {
    const grupos = new Map<string, number[]>();
    entregadas.forEach((t) => { const a = grupos.get(t.area_produccion || '(sin área)') || []; a.push(t.dias_demora!); grupos.set(t.area_produccion || '(sin área)', a); });
    return Array.from(grupos, ([name, arr]) => ({ name, value: mean(arr) || 0 })).sort((a, b) => b.value - a.value);
  }, [entregadas]);

  // % entregas tarde por espacio
  const tardePorEspacio = useMemo(() => {
    const ent = activos.filter((t) => esEntregado(t.estado));
    const tot = countBy(ent, (t) => t.solicitante);
    const tarde = sumBy(ent, (t) => t.solicitante, (t) => (t.estado_entrega === 'tarde' ? 1 : 0));
    const tmap = new Map(tarde.map((x) => [x.name, x.value]));
    return tot.map((x) => ({ name: x.name, value: x.value ? Math.round(((tmap.get(x.name) || 0) / x.value) * 100) : 0 })).sort((a, b) => b.value - a.value);
  }, [activos]);

  const vencidasYProximas = useMemo(
    () => [...activos.filter((t) => t.estado_entrega === 'vencido' || t.estado_entrega === 'proximo')]
      .sort((a, b) => (a.deadline || '').localeCompare(b.deadline || '')),
    [activos]
  );

  return (
    <div className="grid">
      <BoardIntro
        titulo="Tiempos y cumplimiento"
        objetivo="Cuánto tardan los pedidos, con cuánta anticipación se piden y qué tan seguido se entrega en fecha."
        preguntas={['¿Cuánto tarda un pedido?', '¿Con cuánta anticipación piden?', '¿Cumplimos los plazos?']}
      />

      <div className="panel definiciones">
        <div className="def" style={{ borderLeftColor: '#b45309' }}>
          <span className="def-term">Demora</span>
          <span className="def-desc">Días entre que se <b>pide</b> una tarea y se <b>entrega</b>. Mide cuánto tardamos en resolver un pedido: cuanto más baja, más rápido respondemos.</span>
        </div>
        <div className="def" style={{ borderLeftColor: '#0891b2' }}>
          <span className="def-term">Antelación</span>
          <span className="def-desc">Días entre que se <b>pide</b> una tarea y su <b>fecha límite</b>. Mide con cuánto tiempo nos piden las cosas: cuanto más alta, más margen para planificar.</span>
        </div>
      </div>

      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px,1fr))' }}>
        <Kpi label="Demora prom." value={k.demoraProm != null ? `${k.demoraProm}d` : '—'} help="Días promedio entre que se pide una tarea y se entrega." />
        <Kpi label="Demora mediana" value={k.demoraMediana != null ? `${k.demoraMediana}d` : '—'} help="El valor del medio: la mitad de las tareas tarda menos y la mitad más. No se infla con casos extremos." />
        <Kpi label="Antelación prom." value={k.antelacionProm != null ? `${k.antelacionProm}d` : '—'} help="Días promedio entre que se pide una tarea y su fecha límite. Más alto = piden con más tiempo." />
        <Kpi label="Antelación mediana" value={k.antelacionMediana != null ? `${k.antelacionMediana}d` : '—'} help="Valor del medio de la anticipación con que se piden las tareas." />
        <Kpi label="% a tiempo" value={`${k.pctATiempo}%`} tone={k.pctATiempo >= 80 ? 'ok' : k.pctATiempo < 60 ? 'bad' : 'warn'} help="De lo entregado, cuánto llegó en fecha o antes." />
        <Kpi label="% tarde" value={`${k.pctTarde}%`} tone={k.pctTarde > 20 ? 'bad' : undefined} help="De lo entregado, cuánto llegó después de la fecha límite." />
        <Kpi label="Vencidas" value={String(k.vencidas)} tone={k.vencidas ? 'bad' : 'ok'} help="Pasaron la fecha límite y siguen sin entregarse." />
        <Kpi label="Próximas a vencer" value={String(k.proximas)} tone={k.proximas ? 'warn' : 'ok'} help="Vencen dentro de los próximos 3 días." />
      </div>

      {/* Distribuciones (histogramas) */}
      <div className="chart-pair">
        <BarCard title="Histograma de demora (solicitud → entrega)" hint="Cuántas tareas caen en cada rango de días de demora." data={demoraHist} color="#b45309" height={320} tickFontSize={12} />
        <BarCard title="Histograma de antelación (deadline − solicitud)" hint="Con cuánta anticipación se piden las tareas." data={antelHist} color="#0891b2" height={320} tickFontSize={12} />
      </div>

      {/* Demora por dimensión */}
      <div className="chart-pair">
        <BarCard title="Demora promedio por espacio (días)" data={demoraPorEspacio} color="#4f46e5" horizontal height={Math.max(320, demoraPorEspacio.length * 34 + 60)} tickFontSize={12} yAxisWidth={170} />
        <BarCard title="Demora promedio por área (días)" data={demoraPorArea} color="#7c3aed" height={320} tickFontSize={12} />
      </div>

      {/* Cumplimiento */}
      <BarCard title="% entregas tarde por espacio" hint="Porcentaje de entregas que llegaron después de la fecha límite, por solicitante." data={tardePorEspacio} color="#b91c1c" horizontal height={Math.max(300, tardePorEspacio.length * 34 + 60)} tickFontSize={12} yAxisWidth={170} />

      <Card title="Tareas vencidas y próximas a vencer" hint="Ordenadas por deadline ascendente.">
        <div style={{ overflowX: 'auto', maxHeight: 360 }}>
          <table>
            <thead><tr><th>Descripción</th><th>Solicitante</th><th>Estado entrega</th><th>Deadline</th><th className="num">Días p/vencer</th></tr></thead>
            <tbody>
              {vencidasYProximas.map((t) => (
                <tr key={t.id}>
                  <td>{t.descripcion.slice(0, 60)}</td><td>{t.solicitante}</td>
                  <td><span className="badge" style={{ background: ESTADO_ENTREGA_COLORS[t.estado_entrega], color: '#fff' }}>{ESTADO_ENTREGA_LABELS[t.estado_entrega]}</span></td>
                  <td>{fmtDia(t.deadline)}</td><td className="num">{t.dias_para_vencer ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
